import {
  Collection,
  CollectionInput,
  CollectionRepository,
  CollectionSearchInput,
  CollectionUpdateInput,
  ProductToSync,
} from '../domain';
import {
  AccountNotSetError,
  CollectionCreationError,
  CollectionDeletionError,
  CollectionNotFoundError,
  CollectionOperationError,
  CollectionUpdateError,
} from '../domain/collection.errors';

type Synced = {
  id: string;
  synced: boolean;
  error?: string;
};

type SyncedProducts = {
  added: Synced[];
  removed: Synced[];
};

export class CollectionService {
  private account!: string;

  constructor(private collectionRepo: CollectionRepository) {}

  async find(id: string): Promise<Collection> {
    this.validateAccount();
    try {
      const collection = await this.collectionRepo.find(this.account, id);
      if (!collection) {
        throw new CollectionNotFoundError(id);
      }
      return collection;
    } catch (error: any) {
      if (error instanceof CollectionNotFoundError) throw error;
      throw new CollectionOperationError('find', error.message, error);
    }
  }

  async search(
    input: Omit<CollectionSearchInput, 'account'> = { limit: 100 },
  ): Promise<Collection[]> {
    this.validateAccount();
    try {
      return input.search
        ? await this.collectionRepo.search({
            ...input,
            account: this.account,
          })
        : await this.collectionRepo.getAll({
            ...input,
            account: this.account,
          });
    } catch (error: any) {
      throw new CollectionOperationError('search', error.message, error);
    }
  }

  async create(input: Omit<CollectionInput, 'account'>): Promise<Collection> {
    this.validateAccount();
    try {
      return await this.collectionRepo.create({
        ...input,
        account: this.account,
      } as CollectionInput);
    } catch (error: any) {
      throw new CollectionCreationError(error.message, error);
    }
  }

  async update(input: CollectionUpdateInput): Promise<void> {
    this.validateAccount();
    try {
      await this.collectionRepo.update({
        ...input,
        account: this.account,
      } as CollectionUpdateInput);
    } catch (error: any) {
      throw new CollectionUpdateError(input.id, error.message, error);
    }
  }

  async delete(id: string): Promise<void> {
    this.validateAccount();
    try {
      await this.collectionRepo.delete(this.account, id);
    } catch (error: any) {
      throw new CollectionDeletionError(id, error.message, error);
    }
  }

  private async _handleSingleProductSync(
    productSyncData: Omit<ProductToSync, 'account'>,
  ): Promise<Synced> {
    try {
      const success = await this.collectionRepo.manageProduct({
        ...productSyncData,
        account: this.account,
      } as ProductToSync);
      if (success) {
        return { id: productSyncData.productId, synced: true };
      }
      return {
        id: productSyncData.productId,
        synced: false,
        error: 'Sync failed in repository.',
      };
    } catch (e: any) {
      return { id: productSyncData.productId, synced: false, error: e.message };
    }
  }

  async syncProducts(input: {
    toAdd?: Omit<ProductToSync, 'account'>[];
    toRemove?: Omit<ProductToSync, 'account'>[];
  }): Promise<SyncedProducts> {
    this.validateAccount();
    const { toAdd = [], toRemove = [] } = input;

    const addedResults = await Promise.all(
      toAdd.map(p => this._handleSingleProductSync(p)),
    );
    const removedResults = await Promise.all(
      toRemove.map(p => this._handleSingleProductSync(p)),
    );

    return {
      added: addedResults,
      removed: removedResults,
    };
  }

  setAccount(value: string): void {
    this.account = value;
  }

  private validateAccount(): void {
    if (!this.account) {
      throw new AccountNotSetError();
    }
  }
}
